import { FormattedMessage } from 'react-intl'
import { DefaultTheme } from 'styled-components'
import Box from 'components/shared/Box'
import { useResolutions } from 'store/resolutions'
import Info from 'components/shared/Info'
import { useContext, useEffect, useState } from 'react'
import { useSpaCart } from './hooks/useSpaCart'
import OrderedSpa from './fields/OrderedSpa/OrderedSpa'
import { UpsellsContext } from 'routes/Upsell/utils/upsellsContext'
import AddItem from 'components/shared/AddItem'
import SpaEditor from './SpaEditor'
import { useAutoScroll } from 'utils/hooks'
import BirthdayPromotionSpaInfo from 'components/global/promotions/BirthdayPromotion/BirthdayPromotionSpaInfo'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'

interface SpaCreatorProps {
    theme?: DefaultTheme
}

const SpaCreator = ({ theme }: SpaCreatorProps) => {
    const { isMobile } = useResolutions()
    const { calculateScroll, animatePendingScroll } = useAutoScroll()
    const { isBirthdayPromoForSpa } = usePromotions()
    const { cartItems, deleteItemFromCart, isDeleteItemSuccess } = useSpaCart()

    const { setSpaEditing } = useContext(UpsellsContext)
    const [isCreatorExpanded, setIsCreatorExpanded] = useState(
        !cartItems.length
    )
    const [editedCartItemId, setEditedCartItemId] = useState<string | null>()

    const handleItemEdit = (cartId: string) => {
        setEditedCartItemId(cartId)
        setIsCreatorExpanded(true)
    }

    const handleItemDelete = (cartItemId: string) => {
        deleteItemFromCart(cartItemId)
    }

    const handleAddNewItem = () => setIsCreatorExpanded(true)

    const handleSelectionCancel = () => {
        setIsCreatorExpanded(false)
        setEditedCartItemId(null)
        calculateScroll({
            trigerringSection: 'spa',
            trigerringAction: 'cancel',
            hasInstantScroll: true,
        })
    }

    const handleSubmitSuccess = () => {
        setIsCreatorExpanded(false)
        setEditedCartItemId(null)
        calculateScroll({
            trigerringSection: 'spa',
            trigerringAction: 'add',
            // dont wait for render when list with items in cart is already visible
            hasInstantScroll: cartItems.length > 0,
        })
    }

    useEffect(() => {
        setSpaEditing(isCreatorExpanded)
    }, [isCreatorExpanded])

    useEffect(() => {
        if (!isDeleteItemSuccess) return

        calculateScroll({
            trigerringSection: 'spa',
            trigerringAction: 'delete',
            hasInstantScroll: true,
        })
    }, [isDeleteItemSuccess])

    useEffect(() => {
        // after the first render, scroll to align with viewport top of the list with items in cart
        // - if there is scroll to run for corresponding id waiting
        animatePendingScroll('spa-configured')
    }, [])

    return (
        <Box gap={[16, 24]} isFullWidth id="spa-configured">
            {isBirthdayPromoForSpa && (
                <Box mt={[16, 0]} isFullWidth>
                    <BirthdayPromotionSpaInfo />
                </Box>
            )}

            {/* TODO:: move doubled code for this message into variable or component  */}
            {isCreatorExpanded && !!cartItems.length && (
                <Info color="info">
                    <FormattedMessage
                        id="upsells.wellness.first_end_configuring_current_treatment"
                        defaultMessage="Zanim dodasz lub edytujesz zabieg, najpierw dokończ dodawanie poprzedniego."
                    />
                </Info>
            )}
            {cartItems.map(
                (
                    { cartItemId, productId, name, description, unitPrice },
                    index
                ) =>
                    cartItemId !== editedCartItemId ? (
                        <OrderedSpa
                            key={cartItemId}
                            id={cartItemId}
                            name={name}
                            description={description}
                            price={unitPrice}
                            onEdit={() =>
                                cartItemId &&
                                productId &&
                                handleItemEdit(cartItemId)
                            }
                            onDelete={handleItemDelete}
                            isDisabled={isCreatorExpanded}
                            data-testid={`spa-${index}`}
                        />
                    ) : (
                        <SpaEditor
                            key={cartItemId}
                            cartItemId={editedCartItemId}
                            theme={theme}
                            onCancel={handleSelectionCancel}
                            onSuccess={handleSubmitSuccess}
                        />
                    )
            )}
            {!editedCartItemId && isCreatorExpanded && (
                <SpaEditor
                    theme={theme}
                    onCancel={handleSelectionCancel}
                    onSuccess={handleSubmitSuccess}
                />
            )}
            {(!!cartItems.length || !isCreatorExpanded) && (
                <Box
                    direction={isMobile ? 'column-reverse' : 'column'}
                    gap={[16, 24]}
                    isFullWidth
                >
                    {isCreatorExpanded && !!cartItems.length && (
                        <Info color="info">
                            <FormattedMessage
                                id="upsells.wellness.first_end_configuring_current_treatment"
                                defaultMessage="Zanim dodasz lub edytujesz zabieg, najpierw dokończ dodawanie poprzedniego."
                            />
                        </Info>
                    )}
                    {(!isCreatorExpanded || !!cartItems.length) && (
                        <AddItem
                            onClick={handleAddNewItem}
                            testId="spa-button-add-another"
                            isDisabled={isCreatorExpanded}
                        >
                            <FormattedMessage
                                id="upsells.wellness.add_another_treatment"
                                defaultMessage="Dodaj kolejny zabieg"
                            />
                        </AddItem>
                    )}
                </Box>
            )}
        </Box>
    )
}
export default SpaCreator
